<template>
  <section class="intro" v-if="showCalcIntro">
    <h1 class="title">ZESA tariff calculator</h1>
    <h2 class="subtitle is-6">
      Working out just how much ZESA units you will get for a given amount of
      money is hard. So is finding out how much the next x units will cost you.
      Which is why we made this calculator. It can tell you how many units you
      will get for a given amount. It will tell you how much a certain amount of
      units will cost you. First you need to answer some basic questions and
      then we will give you an answer.
    </h2>
  </section>
  <section class="firstQuestion">
    <h3 class="subtitle is-4">You need to answer some basic questions:</h3>
    <h3>
      First we need to know if you have already bought any electricity this
      month:
    </h3>
    <div class="control">
      <label class="radio">
        <input
          name="prior-purchase"
          type="radio"
          value="yesPPurchase"
          v-model="priorPPicked"
          @change="hasBoughtBefore"
        />
        Yes
      </label>
      <label class="radio">
        <input
          name="prior-purchase"
          type="radio"
          value="noPPurchase"
          v-model="priorPPicked"
          @change="hasBoughtBefore"
          checked
        />
        No
      </label>
    </div>
  </section>
  <section v-if="showSQuestion" class="secondQuestion">
    <h3>
      How much have you bought already this month? Your answer can be in
      units/kWH or Zimbabwean dollars.
    </h3>
    <div class="control">
      <label class="radio">
        <input
          type="radio"
          name="prior-purchase-unit"
          value="ppisinzwl"
          v-model="pPuchaseUnit"
        />
        in ZWL $
      </label>
      <label class="radio">
        <input
          type="radio"
          value="ppisinkwh"
          name="prior-purchase-unit"
          pPuchaseUnit
          v-model="pPuchaseUnit"
          checked
        />
        in Units/KwH
      </label>
    </div>
    <div class="columns">
      <div class="column is-one-third has-text-centered">
        <input
          class="input is-large"
          type="number"
          v-model="pPuchaseValue"
          placeholder=""
        />
      </div>
    </div>
  </section>
  <section class="finalQuestion" @keypress.enter="zesaCalculate">
    <h3>
      How much do you want to buy right now? Your answer can be in units/kWH or
      Zimbabwean dollars (ZWL$)
    </h3>
    <div class="control">
      <label class="radio">
        <input
          type="radio"
          name="wanted-zesa"
          value="wantedisinzwl"
          v-model="wantedZESAOption"
        />
        in ZWL $
      </label>
      <label class="radio">
        <input
          type="radio"
          name="wanted-zesa"
          value="wantedisinkwh"
          v-model="wantedZESAOption"
          checked
        />
        in Units/KwH
      </label>
    </div>
    <div class="columns">
      <div class="column is-one-third has-text-centered">
        <input
          class="input is-large"
          type="number"
          v-model="wantedZESA"
          required
          @keypress.enter="zesaCalculate"
        />
        <p
          class="has-vivid-red-color has-text-color"
          style="font-size: 18px"
          v-if="wantedZESAInputError"
        >
          {{ wantedZESAInputError }}
        </p>
      </div>
    </div>
    <div
      @click="zesaCalculate"
      class="wp-block-button has-custom-width wp-block-button__width-75 has-custom-font-size"
      style="font-size: 22px"
    >
      <a
        class="wp-block-button__link has-white-color has-luminous-vivid-orange-background-color has-text-color has-background wp-element-button"
        style="border-radius: 8px"
        >Calculate</a
      >
    </div>
  </section>
  <section class="showResults" v-if="showCalcResults">
    <div class="mb-4">
      <p>{{ finalStatement }}</p>
    </div>
    <div>
      <BuyZESA
        :zesaunits="zesaunits"
        @brokenarrow="hideBuyZESA"
        v-if="!buyZESAIsHidden"
      />
    </div>

    <div id="zesa-results-table"></div>
    <div v-if="haveZESAData">
      <strong>
        <em>Tariffs last updated on {{ rawZESAData?.data?.Date }}</em></strong
      >
    </div>
  </section>
</template>
<script>
import { zesaTariffBands } from "../assets/js/zesa_bands.js";
import { Grid } from "gridjs";
import { prepareResultsTable } from "../assets/js/zesa-helper-functions.js";
import BuyZESA from "./BuyZESA.vue";
import getZESATariffs from "../composables/getTariffData";
import spitBands from "../assets/js/spitBands";

// import Grid from "gridjs-vue";

export default {
  name: "ZESACalculator",
  components: {
    BuyZESA,
  },
  data() {
    return {
      //Get Data form our API
      haveZESAData: false,
      rawZESAData: null,
      //Control state here
      showProgress: true,
      showCalcIntro: true,
      showFQuestion: true,
      showSQuestion: false,
      showFinalQuestion: false,
      showCalcResults: true,
      showCalcIntro: true,
      recalCulate: false,
      buyZESAIsHidden: false,
      //Data variables here
      priorPPicked: null, //Has the customer bought electricity this month?
      wantedZESAInputError: null,
      pPuchaseUnit: "ppisinkwh",
      pPuchaseValue: 0.0,
      wantedZESA: null, //Wanted Value in kWH or ZWL$
      wantedZESAOption: "wantedisinkwh",
      ourProgress: 0,
      zesaunits: null, //This value will be used in our offer call to action
      finalStatement: null,
      zesaTariffBands: zesaTariffBands,
      resultsTable: {
        columns: [
          "Band",
          "Units",
          "Cost/Unit ZLW$",
          "Cost Excl REA",
          "REA ZWL$",
          "Total Charge (ZWL$)",
          "Units Allocation",
        ],
        data: [],
        resizable: true,
        fixedHeader: true,
      },
      resultsTableInstance: null,
    };
  },
  mounted() {
    //Get the tariffs
    getZESATariffs()
      .then((res) => {
        if (res.success) {
          console.log(res);
          this.rawZESAData = res;
          this.haveZESAData = true;

          for (const key in this.zesaTariffBands) {
            const data = spitBands(key, this.rawZESAData?.data?.bands);
            // console.log(key, this.rawZESAData?.data?.bands);
            this.zesaTariffBands[key]["tariff"] = data.zwl_price;
          }
        } else {
          console.log("Nay!");
        }
      })
      .finally((error) => {
        console.log("Wow!");
      });
  },
  methods: {
    zesaCalculate() {
      //Check to see if we have a valid value for wanted ZESA
      if (
        (this.recalCulate === true) &
        (this.wantedZESA === "" || this.wantedZESA === null)
      ) {
        this.wantedZESAInputError = "You need to enter a valid number!";
        this.finalStatement =
          "I don't understand what you want please enter either the units you want to buy or the amount you want to spend.";
        this.resultsTable.data = prepareResultsTable(0, 0); //reset the results table
        this.resultsTableInstance.updateConfig({
          data: this.resultsTable.data,
        });
        this.resultsTableInstance.forceRender(); //Show empty table. If recalculate is set it means we have already drawn the table
        return;
      } else if (this.wantedZESA === "" || this.wantedZESA === null) {
        this.wantedZESAInputError = "You need to enter a valid number!";

        return;
      } else {
        this.wantedZESAInputError = null;
      }
      //Take into account what customer has bought before
      let unitsBoughtbefore = 0;
      let zesaWantedNow = 0;

      if (this.priorPPicked === "yesPPurchase") {
        switch (this.pPuchaseUnit) {
          case "ppisinkwh":
            unitsBoughtbefore = Number(this.pPuchaseValue);
            break;
          case "ppisinzwl":
            unitsBoughtbefore = this.zwlToUnits(Number(this.pPuchaseValue));
            break;
          default:
            break;
        }
      }

      //Get what the customer wants to buy now
      if (this.wantedZESAOption === "wantedisinkwh") {
        zesaWantedNow = this.wantedZESA; //use internal variable we don't want to mess up original
      } else if (this.wantedZESAOption === "wantedisinzwl") {
        zesaWantedNow = this.zwlToUnits(this.wantedZESA);
      }

      this.resultsTable.data = prepareResultsTable(
        unitsBoughtbefore,
        zesaWantedNow
      );
      //Build final statement and prime our call to action offer
      if (this.wantedZESAOption === "wantedisinkwh") {
        this.finalStatement = `Based on what you have told us, ${zesaWantedNow} Units (kWH) will cost you $${this.resultsTable.data[6][5]} ZWL see the breakdown below for more details`;
        this.zesaunits = this.resultsTable.data[6][5];
      } else if (this.wantedZESAOption === "wantedisinzwl") {
        //We have already messed up internal value here so let's get original ZWL amount from Global value
        this.finalStatement = `Based on what you have told us, $${this.wantedZESA} ZWL will get you about ${this.resultsTable.data[6][6]} Units (kWH). See the breakdown below for more details`;
        this.zesaunits = this.wantedZESA;
      }
      //show the results table

      if (this.recalCulate !== true) {
        this.resultsTableInstance = new Grid(this.resultsTable).render(
          document.getElementById("zesa-results-table")
        );
        this.recalCulate = true;
      } else if (this.recalCulate === true) {
        //If the results screen is hidden e.g. after invalid input reshow it

        //We have done this  before
        this.resultsTableInstance.updateConfig({
          data: this.resultsTable.data,
        });
        this.resultsTableInstance.forceRender();
      }
    },
    hasBoughtBefore() {
      if (this.priorPPicked === "yesPPurchase") {
        this.showSQuestion = true;
      } else if (this.priorPPicked === "noPPurchase") {
        this.showSQuestion = false;
      }
    },
    zwlToUnits(zwlAmount) {
      let unitsToadd = [];
      //First hop
      if (zwlAmount < this.zesaTariffBands.first50.bandCombinedZWL()) {
        unitsToadd.push(
          zwlAmount / this.zesaTariffBands.first50.combinedTariff()
        );
        zwlAmount = 0; //reset zwlAmount
      } else if (zwlAmount >= this.zesaTariffBands.first50.bandCombinedZWL()) {
        unitsToadd.push(this.zesaTariffBands.first50.bandUnits);
        zwlAmount = zwlAmount - this.zesaTariffBands.first50.bandCombinedZWL();
      }

      if (zwlAmount < this.zesaTariffBands.secondBand.bandCombinedZWL()) {
        unitsToadd.push(
          zwlAmount / this.zesaTariffBands.secondBand.combinedTariff()
        );
        zwlAmount = 0; //reset zwlAmount
      } else if (
        zwlAmount >= this.zesaTariffBands.secondBand.bandCombinedZWL()
      ) {
        unitsToadd.push(this.zesaTariffBands.secondBand.bandUnits);
        zwlAmount =
          zwlAmount - this.zesaTariffBands.secondBand.bandCombinedZWL();
      }
      //Still got money? Let's go to third band
      if (zwlAmount < this.zesaTariffBands.thirdBand.bandCombinedZWL()) {
        unitsToadd.push(
          zwlAmount / this.zesaTariffBands.thirdBand.combinedTariff()
        );
        zwlAmount = 0; //reset zwlAmount
      } else if (
        zwlAmount >= this.zesaTariffBands.thirdBand.bandCombinedZWL()
      ) {
        unitsToadd.push(this.zesaTariffBands.thirdBand.bandUnits);
        zwlAmount =
          zwlAmount - this.zesaTariffBands.thirdBand.bandCombinedZWL();
      }
      //If we have money let's go for fourth band

      if (zwlAmount < this.zesaTariffBands.fourthBand.bandCombinedZWL()) {
        unitsToadd.push(
          zwlAmount / this.zesaTariffBands.fourthBand.combinedTariff()
        );
        zwlAmount = 0; //reset zwlAmount
      } else if (
        zwlAmount >= this.zesaTariffBands.fourthBand.bandCombinedZWL()
      ) {
        unitsToadd.push(this.zesaTariffBands.fourthBand.bandUnits);
        zwlAmount =
          zwlAmount - this.zesaTariffBands.fourthBand.bandCombinedZWL();
      }
      //If we have money let's go for fifth band
      if (zwlAmount < this.zesaTariffBands.fifthBand.bandCombinedZWL()) {
        unitsToadd.push(
          zwlAmount / this.zesaTariffBands.fifthBand.combinedTariff()
        );
        zwlAmount = 0; //reset zwlAmount
      } else if (
        zwlAmount >= this.zesaTariffBands.fifthBand.bandCombinedZWL()
      ) {
        unitsToadd.push(this.zesaTariffBands.fifthBand.bandUnits);
        zwlAmount =
          zwlAmount - this.zesaTariffBands.fifthBand.bandCombinedZWL();
      }

      //Calculate overage using final band
      if (zwlAmount > 0) {
        unitsToadd.push(
          zwlAmount / this.zesaTariffBands.finalBand.combinedTariff()
        );
        zwlAmount = 0; //just being safe
      }

      //Whatever happened above let's find sum of stuff in our array
      return unitsToadd.reduce((a, b) => a + b, 0);
    },
    unitsToZWL(units) {
      let zwlToAdd = [];
      if (units < this.zesaTariffBands.first50.bandUnits) {
        zwlToAdd.push(units * this.zesaTariffBands.first50.combinedTariff());
        units = 0;
      } else if (units >= this.zesaTariffBands.first50.bandUnits) {
        zwlToAdd.push(this.zesaTariffBands.first50.bandCombinedZWL());
        units = units - this.zesaTariffBands.first50.bandUnits;
      }
      //More than 50 let's go to second band

      if ((units < this.zesaTariffBands.secondBand.bandUnits) & (units !== 0)) {
        zwlToAdd.push(units * this.zesaTariffBands.secondBand.combinedTariff());
        units = 0;
      } else if (units >= this.zesaTariffBands.secondBand.bandUnits) {
        zwlToAdd.push(this.zesaTariffBands.secondBand.bandCombinedZWL());
        units = units - this.zesaTariffBands.secondBand.bandUnits;
      }
      //Go to third band
      if ((units < this.zesaTariffBands.thirdBand.bandUnits) & (units !== 0)) {
        zwlToAdd.push(units * this.zesaTariffBands.thirdBand.combinedTariff());
        units = 0;
      } else if (units >= this.zesaTariffBands.thirdBand.bandUnits) {
        zwlToAdd.push(this.zesaTariffBands.thirdBand.bandCombinedZWL());
        units = units - this.zesaTariffBands.thirdBand.bandUnits;
      }
      //Go for fourth band
      if ((units < this.zesaTariffBands.fourthBand.bandUnits) & (units !== 0)) {
        zwlToAdd.push(units * this.zesaTariffBands.fourthBand.combinedTariff());
        units = 0;
      } else if (units >= this.zesaTariffBands.fourthBand.bandUnits) {
        zwlToAdd.push(this.zesaTariffBands.fourthBand.bandCombinedZWL());
        units = units - this.zesaTariffBands.fourthBand.bandUnits;
      }
      //Go for fifth band
      //Go to third band
      if ((units < this.zesaTariffBands.fifthBand.bandUnits) & (units !== 0)) {
        zwlToAdd.push(units * this.zesaTariffBands.fifthBand.combinedTariff());
        units = 0;
      } else if (units >= this.zesaTariffBands.fifthBand.bandUnits) {
        zwlToAdd.push(this.zesaTariffBands.fifthBand.bandCombinedZWL());
        units = units - this.zesaTariffBands.fifthBand.bandUnits;
      }
      //More than subsidised units
      if (units > 0) {
        zwlToAdd.push(units * this.zesaTariffBands.finalBand.combinedTariff());
        units = 0;
      }
      //Add up all stuff
      let sum = zwlToAdd.reduce((a, b) => a + b, 0);
    },
    showResults() {
      new Promise(function () {
        this.showCalcResults = true;
      });
    },
    hideBuyZESA() {
      this.buyZESAIsHidden = true;
      console.log("Code broken arrow is received sir!");
    },
  },
};
</script>

<style></style>
